import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'

// Separate RTK Query API: TextQL has its own base URL + Bearer auth, so it does not
// share the main api slice (src/api/index.js). Registered in src/providers/store.js
// and src/middleware/reducers/index.js. In dev, an empty base URL routes /v2 through
// src/setupProxy.js to app.textql.com (in deploys, the nginx reverse-proxy does it).
// Questions are asked through the SSE client in src/api/textqlStreamClient.ts — this
// slice only holds the plain request/response endpoints.
const TEXTQL_BASE_URL = process.env.REACT_APP_TEXTQL_API_URL || ''
const TEXTQL_API_KEY = process.env.REACT_APP_TEXTQL_API_KEY

const rawBaseQuery = fetchBaseQuery({
  baseUrl: TEXTQL_BASE_URL,
  prepareHeaders: headers => {
    if (TEXTQL_API_KEY) {
      headers.set('authorization', `Bearer ${TEXTQL_API_KEY}`)
    }
    return headers
  },
})

// Retry transient failures (load-balancer blips) up to 2x. Bail immediately on 4xx
// (auth / bad request) so we don't loop on real errors.
const baseQueryWithRetry = retry(
  async (
    args: Parameters<typeof rawBaseQuery>[0],
    api: Parameters<typeof rawBaseQuery>[1],
    extraOptions: Parameters<typeof rawBaseQuery>[2]
  ) => {
    const result = await rawBaseQuery(args, api, extraOptions)
    const { error } = result
    if (error && typeof error.status === 'number' && error.status >= 400 && error.status < 500) {
      retry.fail(error)
    }
    return result
  },
  { maxRetries: 2 }
)

export const textqlApi = createApi({
  reducerPath: 'textqlApi',
  baseQuery: baseQueryWithRetry,
  endpoints: builder => ({
    cancelChatStream: builder.mutation<void, { chatId: string }>({
      query: body => ({
        url: `/v2/chats/${body.chatId}/cancel`,
        method: 'POST',
      }),
    }),
  }),
})

export const { useCancelChatStreamMutation } = textqlApi
